'use client';

import { ClerkProvider } from '@clerk/nextjs';
import { dark } from '@clerk/themes';
import { useTheme } from 'next-themes';
import { usePathname } from 'next/navigation';
import { useEffect, useState } from 'react';

export function ClerkProviderWrapper({ children }: { children: React.ReactNode }) {
  const { resolvedTheme } = useTheme();
  const pathname = usePathname();
  const [mounted, setMounted] = useState(false);
  const [afterSignOutUrl, setAfterSignOutUrl] = useState<string>();

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    setAfterSignOutUrl(
      pathname === '/select-org' ? `${pathname}${window.location.search}` : undefined,
    );
  }, [pathname]);

  // Light is the default theme, so assume light until mounted to avoid a
  // dark→light flash on the signed-out auth pages.
  const isLight = mounted ? resolvedTheme === 'light' : true;
  const clerkTheme = isLight ? undefined : dark;

  // Brand tokens per mode, mirroring globals.css. Clerk derives its shade
  // scales from these, so they must be concrete colors (not CSS vars).
  const palette = isLight
    ? {
        colorPrimary: '#1c2024', // charcoal
        colorBackground: '#fcfbf7', // warm off-white surface
        colorText: '#1c2024',
        colorTextSecondary: '#60646c',
        colorInputBackground: '#fcfbf7',
        colorInputText: '#1c2024',
        colorNeutral: '#1c2024',
      }
    : {
        colorPrimary: '#edeef0', // off-white
        colorBackground: '#26282b', // charcoal card
        colorText: '#edeef0',
        colorTextSecondary: '#9aa0a6',
        colorInputBackground: '#26282b',
        colorInputText: '#edeef0',
        colorNeutral: '#edeef0',
      };

  return (
    <ClerkProvider
      afterSignOutUrl={afterSignOutUrl}
      appearance={{
        theme: clerkTheme,
        variables: {
          // Square corners + Inter + neutral primary (charcoal on light,
          // off-white on dark) to match the brand style system.
          borderRadius: '0px',
          fontFamily: 'Inter, ui-sans-serif, system-ui, sans-serif',
          ...palette,
        },
      }}
      localization={{
        formFieldInputPlaceholder__confirmDeletionUserAccount: 'delete account',
        organizationProfile: {
          profilePage: {
            dangerSection: {
              deleteOrganization: {
                messageLine2:
                  'This action is permanent and irreversible. Any outstanding usage will be billed.',
              },
            },
          },
        },
      }}
    >
      {children}
    </ClerkProvider>
  );
}
